import type { ColumnDef } from "@tanstack/react-table"
import { useMemo } from "react"

import type { ApAgingInvoice } from "../types/apAgingReport"

import StyledChip from "@/components/common/Chip/Chip"
import { DATE_FORMAT } from "@/constants/formats"
import { ApAgingBucket, InvoiceStatus, InvoiceType } from "@/graphql/codegen/graphql"
import { INVOICE_STATUS_LABELS } from "@/screens/Accounting/subfeatures/Invoices/constants/labels"
import { formatCurrencyWithCents } from "@/utils/currency"
import { formatDate } from "@/utils/date"

// The Aging Bucket column uses the short form of the bucket names.
const AGING_BUCKET_TO_SHORT_LABEL: Record<ApAgingBucket, string> = {
  [ApAgingBucket.Current]: "Current",
  [ApAgingBucket.Days_1_30]: "1-30",
  [ApAgingBucket.Days_31_60]: "31-60",
  [ApAgingBucket.Days_61_90]: "61-90",
  [ApAgingBucket.Over_90]: "Over 90",
}

const INVOICE_TYPE_TO_LABEL: Record<InvoiceType, string> = {
  [InvoiceType.General]: "General Invoice",
  [InvoiceType.Po]: "PO Invoice",
}

const INVOICE_TYPE_TO_CHIP_CLASS_NAME: Record<InvoiceType, string> = {
  [InvoiceType.General]: "bg-primary-200 text-gray-900",
  [InvoiceType.Po]: "bg-tertiary-purple-300 text-gray-900",
}

// Only the statuses the report contains have a colour.
const STATUS_TO_CHIP_CLASS_NAME: Partial<Record<InvoiceStatus, string>> = {
  [InvoiceStatus.Approved]: "bg-tertiary-green-100 text-gray-900",
  [InvoiceStatus.PendingApproval]: "bg-tertiary-yellow-300 text-gray-900",
  [InvoiceStatus.NotApproved]: "bg-secondary-200 text-gray-900",
}

// AK-6964 AC7: Only Days Overdue, Due Date and Status are sortable. Their `id`s are the camel-cased names of the
// `ApAgingInvoiceSortField` values, and each needs an `accessorFn` because TanStack's getCanSort() returns false
// without one.
export const useApAgingInvoiceColumns = (): ColumnDef<ApAgingInvoice>[] =>
  useMemo<ColumnDef<ApAgingInvoice>[]>(
    () => [
      {
        id: "invoiceNumber",
        header: "Invoice #",
        cell: ({ row }) => row.original.invoiceNumber,
        enableSorting: false,
      },
      {
        id: "invoiceType",
        header: "Invoice Type",
        cell: ({ row }) => (
          <StyledChip
            label={INVOICE_TYPE_TO_LABEL[row.original.type]}
            className={INVOICE_TYPE_TO_CHIP_CLASS_NAME[row.original.type]}
            size='small'
          />
        ),
        enableSorting: false,
      },
      {
        id: "creationDate",
        header: "Creation Date",
        cell: ({ row }) => formatDate(row.original.creationDate, DATE_FORMAT.ISO_18601),
        enableSorting: false,
      },
      {
        id: "dueDate",
        accessorFn: (invoice) => invoice.dueDate,
        header: "Due Date",
        cell: ({ row }) => formatDate(row.original.dueDate, DATE_FORMAT.ISO_18601),
        enableSorting: true,
      },
      {
        id: "daysOverdue",
        accessorFn: (invoice) => invoice.daysOverdue,
        header: "Days Overdue",
        cell: ({ row }) => row.original.daysOverdue,
        enableSorting: true,
        meta: { align: "right" },
      },
      {
        id: "agingBucket",
        header: "Aging Bucket",
        cell: ({ row }) => AGING_BUCKET_TO_SHORT_LABEL[row.original.agingBucket],
        enableSorting: false,
      },
      {
        id: "status",
        accessorFn: (invoice) => invoice.status,
        header: "Status",
        cell: ({ row }) => (
          <StyledChip
            label={INVOICE_STATUS_LABELS[row.original.status]}
            className={STATUS_TO_CHIP_CLASS_NAME[row.original.status]}
            size='small'
          />
        ),
        enableSorting: true,
      },
      {
        id: "outstandingAmount",
        header: "Amount",
        cell: ({ row }) => formatCurrencyWithCents(row.original.outstandingAmount),
        enableSorting: false,
        meta: { align: "right" },
      },
    ],
    []
  )
